import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import Slider from 'react-slick';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './index.css';
import { CategoryListItemExt, useCategoriesListQuery, usePostsListQuery } from 'api';
import { Spinner } from 'components';
import { POSTS_REQUEST_LIMIT } from 'constants/index';
import { Links } from 'settings';
import { theme } from 'styles';
import { InstanceType } from 'types';

import { EmptyState } from '../EmptyState';
import { PostListItem } from '../PostListItem';
import {
  ListTitle,
  ListW,
  PinHeaderContainer,
  SliderOuterW,
  SliderW,
  SlideW,
  SpinnerContainer,
} from './styled';

const POST_FEED_TYPES = [InstanceType.news, InstanceType.document, InstanceType.polls];

export function PostsList({
  isMobilePreview = false,
}: {
  isMobilePreview?: boolean;
}): ReactElement {
  const { t } = useTranslation();

  const { posts, isLoading, lastItemRef, isFetchingNextPage, isEmptyList } = usePostsListQuery({
    type: POST_FEED_TYPES,
    place: 'newsfeed',
    limit: POSTS_REQUEST_LIMIT,
    include_reactions: true,
    pin: false,
  });

  const {
    posts: pinnedPosts,
    isLoading: isPinnedPostLoading,
    lastItemRef: lastPinnedItemRef,
  } = usePostsListQuery({
    type: POST_FEED_TYPES,
    place: 'newsfeed',
    limit: POSTS_REQUEST_LIMIT,
    pin: true,
    include_reactions: true,
  });

  const {
    categoriesList,
    isLoading: isCategoryListLoading,
  }: { categoriesList: CategoryListItemExt[]; isLoading: boolean } = useCategoriesListQuery();

  const sliderSettings = useMemo(
    () => ({
      dots: true,
      infinite: pinnedPosts.length > 1,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: true,
      speed: 600,
      autoplaySpeed: 4000,
      cssEase: 'ease-in-out',
      adaptiveHeight: true,
      arrows: false,
    }),
    [pinnedPosts.length],
  );

  return (
    <ListW>
      {(isLoading || isCategoryListLoading || isPinnedPostLoading) && (
        <SpinnerContainer>
          <Spinner size="2.5rem" />
        </SpinnerContainer>
      )}
      {pinnedPosts?.length > 0 && !isMobilePreview ? (
        <>
          <PinHeaderContainer>
            <ListTitle>{t('we.pinnedPosts.title')}</ListTitle>
            <NavLink
              style={{
                fontSize: '14px',
                textDecoration: 'underline',
                color: theme.palette.primary.main,
                alignSelf: 'center',
              }}
              to={Links.we.pinnedPosts}
            >
              {t('we.posts.list.showAllPinned')}
            </NavLink>
          </PinHeaderContainer>
          <SliderW>
            <Slider {...sliderSettings}>
              {pinnedPosts.map((post, idx) => (
                <SliderOuterW key={post.id}>
                  <SlideW>
                    <PostListItem
                      key={post.id}
                      lastItemRef={idx === posts.length - 1 ? lastPinnedItemRef : undefined}
                      categoriesList={categoriesList}
                      isSlider
                      hideAttachments
                      {...post}
                    />
                  </SlideW>
                </SliderOuterW>
              ))}
            </Slider>
          </SliderW>
        </>
      ) : (
        <></>
      )}
      <PinHeaderContainer>
        <ListTitle>{t('we.posts.list.subTitle1')}</ListTitle>
      </PinHeaderContainer>

      {((!isLoading && posts.length === 0) || isEmptyList) && <EmptyState />}

      {posts.map((post, idx) => (
        <PostListItem
          key={post.id}
          lastItemRef={idx === posts.length - 1 ? lastItemRef : undefined}
          categoriesList={categoriesList}
          {...post}
        />
      ))}

      {isFetchingNextPage && <Spinner wrapperSx={{ bottom: '0', top: 'unset' }} />}
    </ListW>
  );
}
